import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://xentrios.com/#organization',
      name: 'Xentrios',
      url: 'https://xentrios.com',
      description: 'Cloud-native dental PMS and operating platform for multi-location DSOs.',
      sameAs: ['https://revtechpartners.com', 'https://nationaldatasolutions.com'],
    },
    {
      '@type': 'Organization',
      '@id': 'https://revtechpartners.com/#organization',
      name: 'RevTech Partners',
      url: 'https://revtechpartners.com',
      description: 'RCM intelligence platform for U.S. dental groups: InsightFlow, DenialIQ, ClaimVision, PayerIQ, ReconIQ.',
    },
    {
      '@type': 'Organization',
      '@id': 'https://nationaldatasolutions.com/#organization',
      name: 'National Data Solutions',
      url: 'https://nationaldatasolutions.com',
      description: 'Dental data warehouse, ETL, PMS integrations, Power BI, and enterprise data governance.',
    },
    {
      '@type': 'Organization',
      name: 'MaximizeRCM',
      description: 'RCM advisory, billing consulting, payer contract negotiation, and operational execution for dental groups.',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'RevTech + NDS + Xentrios Integrated Dental Stack',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'An integrated dental operating intelligence stack for U.S. DSOs combining RCM automation, data warehousing, denial management, reconciliation, analytics, and cloud-native practice management.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@id': 'https://xentrios.com/#organization',
      },
    },
  ],
};

export default function StackPage() {
  const cmsPage = useCmsPage('xentrios', 'stack');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'An integrated dental technology platform for U.S. DSOs.');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const heroCta = c(cmsPage, 'hero', 'cta_text', 'Request Demo');
  return (
    <div>
      <Helmet>
        <title>RevTech + NDS + Xentrios — Integrated Dental Technology Stack for DSOs</title>
        <meta
          name="description"
          content="RevTech Partners, National Data Solutions, and Xentrios form an integrated dental operating intelligence stack for U.S. DSOs: RCM, denial management, data warehouse, analytics, PMS, and AI agents — all from one ecosystem."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">The Complete Dental Tech Stack</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              {heroCta}
            </Link>
            <a
              href="#stack-brands"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Stack
            </a>
          </div>
        </div>
      </section>

      {/* Why an integrated stack? */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Problem</p>
          <h2>DSOs are held back by fragmented point solutions.</h2>
          <div className="xen-two-col" style={{ marginTop: '2rem', gap: '3rem' }}>
            <div>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>What DSOs deal with today</h3>
              <p>
                The average U.S. dental group operates with 4 to 6 separate technology vendors: a legacy PMS,
                a standalone RCM tool, a third-party analytics platform, a data integration consultant, a
                clearinghouse, and sometimes a billing services firm. None of these systems were built to talk
                to each other.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The result is fragmented workflows, inconsistent data, duplicate entry across systems, slow
                claim resolution, and a reporting environment where your operations team can't get a reliable
                answer to basic questions like "what is our AR by payer across all locations this month?"
              </p>
              <p style={{ marginTop: '1rem' }}>
                Every vendor has a different implementation timeline, a different support contact, a different
                contract, and a different data model. When something breaks at the intersection of two systems,
                each vendor points at the other.
              </p>
            </div>
            <div>
              <h3 style={{ color: 'var(--xen-accent)', marginBottom: '1rem' }}>What the integrated stack delivers</h3>
              <p>
                RevTech + NDS + Xentrios is purpose-built to eliminate that fragmentation for United States
                dental groups. The three platforms share a common data model, a unified patient and claim
                identity layer, and a single implementation team.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Clinical data created in Xentrios flows directly into the NDS DataVault. Claims processed
                through the RevTech suite reference the same patient record. Denials worked in DenialIQ
                reconcile against the same payment data in ReconIQ. Power BI dashboards built on the NDS
                layer show metrics from all three platforms in one view.
              </p>
              <p style={{ marginTop: '1rem' }}>
                One contract negotiation. One implementation project. One support relationship. One data
                governance model. For a DSO operating 20, 50, or 200 locations across the United States,
                that consolidation has measurable operational and financial value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand hierarchy */}
      <section id="stack-brands" className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Four Brands, One Stack</p>
          <h2>Built by people who understand dental operations.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Each brand owns a distinct layer of the dental technology stack. Together they cover every
            operational and financial workflow a U.S. DSO requires.
          </p>
          <div className="xen-features" style={{ marginTop: '2rem' }}>
            <div
              className="xen-feature"
              style={{ borderTop: '4px solid var(--xen-navy)', textAlign: 'left' }}
            >
              <div className="xen-feature-icon" style={{ fontSize: '1.5rem' }}>🦷</div>
              <h3 style={{ color: 'var(--xen-navy)' }}>Xentrios</h3>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#444' }}>Cloud-Native Dental PMS</p>
              <p>
                The practice management system built from day one for multi-location DSOs. Xentrios handles
                scheduling, clinical charting, treatment planning, billing, claims, pre-authorization, patient
                communications, and enterprise reporting — all in one cloud-native platform.
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                AI workforce support is embedded throughout: clinical documentation assistance, insurance
                eligibility pre-screening, coding suggestions, and appointment optimization. Built to reduce
                administrative burden on clinical staff without obscuring decision-making.
              </p>
            </div>
            <div
              className="xen-feature"
              style={{ borderTop: '4px solid #1976d2', textAlign: 'left' }}
            >
              <div className="xen-feature-icon" style={{ fontSize: '1.5rem' }}>🗄️</div>
              <h3 style={{ color: '#1976d2' }}>National Data Solutions</h3>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#444' }}>Dental Data Warehouse & ETL</p>
              <p>
                NDS provides the enterprise data infrastructure layer for U.S. dental groups. The DataVault
                platform extracts, normalizes, and warehouses data from Dentrix, Eaglesoft, Open Dental,
                CareStack, Planet DDS, and Xentrios into a single governed data model.
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                Pre-built Power BI report packages, custom KPI development, Exocomp automation agents,
                and a data governance framework that meets the needs of PE-backed groups and enterprise DSOs.
              </p>
            </div>
            <div
              className="xen-feature"
              style={{ borderTop: '4px solid #388e3c', textAlign: 'left' }}
            >
              <div className="xen-feature-icon" style={{ fontSize: '1.5rem' }}>📊</div>
              <h3 style={{ color: '#388e3c' }}>RevTech Partners</h3>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#444' }}>RCM Intelligence Suite</p>
              <p>
                RevTech delivers the analytics and automation layer for the revenue cycle. The suite includes
                InsightFlow (AR analytics), DenialIQ (denial management), ClaimVision (claim intelligence),
                PayerIQ (payer contract analytics), and ReconIQ (payment reconciliation).
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                Each product is designed for multi-location dental workflows and U.S. payer-specific logic.
                Native integrations with Xentrios and the NDS DataVault mean no manual data exports or
                separate reporting pipelines.
              </p>
            </div>
            <div
              className="xen-feature"
              style={{ borderTop: '4px solid #e65100', textAlign: 'left' }}
            >
              <div className="xen-feature-icon" style={{ fontSize: '1.5rem' }}>🤝</div>
              <h3 style={{ color: '#e65100' }}>MaximizeRCM</h3>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#444' }}>Advisory & Operational Services</p>
              <p>
                MaximizeRCM provides the human services layer of the stack: RCM advisory, billing team
                training and managed services, payer contract negotiation, operational workflow consulting,
                and implementation support for new locations and acquisitions.
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                For DSOs that want software plus execution capability — a team that understands how the
                technology works and how dental billing actually gets done — MaximizeRCM bridges that gap.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture diagram */}
      <section className="xen-section xen-section-light xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow">How It Works</p>
          <h2>From chair to cash — one connected data layer.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Every transaction in the stack flows through a shared data model. Clinical events, claims,
            payments, and denials are all visible in real time across the platform.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0',
              flexWrap: 'wrap',
              marginTop: '2rem',
            }}
          >
            {[
              { label: 'Xentrios PMS', sub: 'Scheduling · Clinical · Billing', color: 'var(--xen-navy)' },
              { label: 'NDS DataVault', sub: 'ETL · Normalization · Governance', color: '#1976d2' },
              { label: 'RevTech Analytics', sub: 'InsightFlow · DenialIQ · ReconIQ', color: '#388e3c' },
              { label: 'Reporting & AI', sub: 'Power BI · Dashboards · Alerts', color: '#e65100' },
            ].map((node, i, arr) => (
              <React.Fragment key={node.label}>
                <div
                  style={{
                    background: node.color,
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '1.25rem 1.5rem',
                    minWidth: '160px',
                    textAlign: 'center',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem' }}>{node.label}</div>
                  <div style={{ fontSize: '0.78rem', opacity: 0.85, lineHeight: 1.4 }}>{node.sub}</div>
                </div>
                {i < arr.length - 1 && (
                  <div
                    style={{
                      fontSize: '1.75rem',
                      color: '#999',
                      padding: '0 0.5rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <p style={{ marginTop: '2rem', color: '#666', fontSize: '0.95rem', maxWidth: '640px', margin: '2rem auto 0' }}>
            Data flows in real time. A claim created in Xentrios is immediately visible in InsightFlow, trackable
            in DenialIQ if denied, and reconciled in ReconIQ when paid — all without manual exports or
            third-party connectors.
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Integrated stack vs. point solutions</h2>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.95rem',
                boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <thead>
                <tr style={{ background: 'var(--xen-navy)', color: '#fff' }}>
                  <th style={{ padding: '1rem 1.25rem', textAlign: 'left', fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: '1rem 1.25rem', textAlign: 'center', fontWeight: 600 }}>
                    RevTech + NDS + Xentrios
                  </th>
                  <th style={{ padding: '1rem 1.25rem', textAlign: 'center', fontWeight: 600 }}>
                    4 Separate Vendors
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Single login across all modules', '✓ Unified SSO', '✗ 4+ login portals'],
                  ['Shared patient data layer', '✓ Common patient identity', '✗ Siloed records per system'],
                  ['Real-time claim visibility', '✓ Live across all tools', '✗ Batch exports required'],
                  ['Cross-location AR roll-up', '✓ Native enterprise reporting', '✗ Manual aggregation in spreadsheets'],
                  ['Pre-built PMS connectors', '✓ Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, Xentrios', '⚠ Varies by vendor'],
                  ['Unified support contact', '✓ One team owns the stack', '✗ Vendor-to-vendor blame cycles'],
                  ['Implementation timeline', '✓ Single coordinated rollout', '✗ 4 separate timelines and go-lives'],
                  ['Total contract complexity', '✓ One master agreement', '✗ 4 contracts, 4 renewal cycles, 4 rate negotiations'],
                ].map(([feature, integrated, separate], i) => (
                  <tr
                    key={feature}
                    style={{ background: i % 2 === 0 ? '#f9f9fb' : '#fff', borderBottom: '1px solid #eee' }}
                  >
                    <td style={{ padding: '0.875rem 1.25rem', fontWeight: 500, color: 'var(--xen-navy)' }}>
                      {feature}
                    </td>
                    <td
                      style={{
                        padding: '0.875rem 1.25rem',
                        textAlign: 'center',
                        color: '#388e3c',
                        fontWeight: 500,
                      }}
                    >
                      {integrated}
                    </td>
                    <td
                      style={{
                        padding: '0.875rem 1.25rem',
                        textAlign: 'center',
                        color: '#c62828',
                        fontWeight: 500,
                      }}
                    >
                      {separate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Target buyer */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container" style={{ maxWidth: '780px' }}>
          <h2>Built for DSOs operating 5 to 500+ locations across the United States.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1.25rem' }}>
            The stack is designed for groups at every growth stage — from a regional group acquiring its
            tenth location to a national platform managing hundreds of practices.
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '2rem auto 0',
              maxWidth: '600px',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--xen-teal)', fontWeight: 700, fontSize: '1.1rem', marginTop: '2px' }}>✓</span>
              <span>
                <strong>Private equity-backed DSOs</strong> that need consolidated reporting, audit-ready
                financials, and a technology stack that scales with acquisitions without re-implementation.
              </span>
            </li>
            <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--xen-teal)', fontWeight: 700, fontSize: '1.1rem', marginTop: '2px' }}>✓</span>
              <span>
                <strong>Regional dental groups</strong> standardizing operations across multiple locations
                who need one system their entire team can learn, use, and trust — without custom integrations
                that break on every software update.
              </span>
            </li>
            <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--xen-teal)', fontWeight: 700, fontSize: '1.1rem', marginTop: '2px' }}>✓</span>
              <span>
                <strong>Revenue cycle leaders and CFOs</strong> who are tired of defending a patchwork of
                point solutions that can't answer basic questions about payer mix, denial rates, or net
                collection performance across the enterprise.
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center" style={{ paddingTop: '3rem', paddingBottom: '4rem' }}>
        <div className="xen-container">
          <h2>See how the stack fits your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer who can walk you through the full stack and show you what
            consolidation looks like for your specific PMS and payer mix.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
